import { useEffect, useRef, useState } from "react";

/**
 * Award-worthy soft cursor: gold dot + trailing ring. Grows/inverts on
 * elements with [data-cursor='hover'] or interactive tags. Uses rAF
 * lerp for buttery motion. Hidden on touch devices.
 */
export default function Cursor() {
    const [enabled, setEnabled] = useState(false);
    const dotRef = useRef(null);
    const ringRef = useRef(null);

    useEffect(() => {
        const canHover = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
        setEnabled(canHover);
        if (!canHover) return;

        const mouse = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
        const ring = { x: mouse.x, y: mouse.y };
        let hovered = false;
        let rafId;

        const onMove = (e) => {
            mouse.x = e.clientX;
            mouse.y = e.clientY;
        };
        const isInteractive = (el) =>
            !!el?.closest(
                "a, button, [role='button'], input, textarea, select, [data-cursor='hover']"
            );
        const onOver = (e) => {
            const h = isInteractive(e.target);
            if (h !== hovered) {
                hovered = h;
                const r = ringRef.current;
                const d = dotRef.current;
                if (r && d) {
                    r.style.width = hovered ? "56px" : "34px";
                    r.style.height = hovered ? "56px" : "34px";
                    r.style.borderColor = hovered
                        ? "rgba(212,175,55,0.9)"
                        : "rgba(212,175,55,0.55)";
                    r.style.background = hovered ? "rgba(212,175,55,0.08)" : "transparent";
                    d.style.opacity = hovered ? "0" : "1";
                }
            }
        };

        const tick = () => {
            ring.x += (mouse.x - ring.x) * 0.18;
            ring.y += (mouse.y - ring.y) * 0.18;
            if (dotRef.current) {
                dotRef.current.style.transform = `translate3d(${mouse.x}px, ${mouse.y}px, 0) translate(-50%, -50%)`;
            }
            if (ringRef.current) {
                ringRef.current.style.transform = `translate3d(${ring.x}px, ${ring.y}px, 0) translate(-50%, -50%)`;
            }
            rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);

        window.addEventListener("mousemove", onMove, { passive: true });
        window.addEventListener("mouseover", onOver, { passive: true });
        document.documentElement.classList.add("has-custom-cursor");

        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseover", onOver);
            document.documentElement.classList.remove("has-custom-cursor");
        };
    }, []);

    if (!enabled) return null;
    return (
        <>
            <div
                ref={ringRef}
                aria-hidden
                className="pointer-events-none fixed top-0 left-0 z-[9999] rounded-full border transition-[width,height,background,border-color] duration-200 ease-out"
                style={{
                    width: 34,
                    height: 34,
                    borderColor: "rgba(212,175,55,0.55)",
                    mixBlendMode: "difference",
                }}
            />
            <div
                ref={dotRef}
                aria-hidden
                className="pointer-events-none fixed top-0 left-0 z-[9999] h-2 w-2 rounded-full bg-[#D4AF37] transition-opacity duration-200"
            />
        </>
    );
}
